import { useEffect, useRef, useState } from 'react';
import { colorToHex, generateCode, toSvg, textStyle, isAutoLayout, cssVariableName, codeConnectOf, connectSnippet, componentSetOf, changesSinceReady, Editor, Renderer2D, boundsOfNodes, type NodeData, type Paint } from '@vellum/core';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';

const r1 = (v: number) => Math.round(v * 10) / 10;

function Copy({ text, children }: { text: string; children?: React.ReactNode }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className="dev-copy"
      title="Copy"
      onClick={() => {
        void navigator.clipboard?.writeText(text);
        setDone(true);
        setTimeout(() => setDone(false), 900);
      }}
    >
      {children ?? text}
      <span className="dev-copied">{done ? 'Copied' : ''}</span>
    </button>
  );
}

function paintLabel(p: Paint) {
  if (p.type === 'SOLID') return colorToHex(p.color);
  if (p.type === 'IMAGE') return 'Image';
  return p.type === 'GRADIENT_LINEAR' ? 'Linear gradient' : 'Radial gradient';
}

/** Dev Mode's right panel: what a developer needs to build the selection. */
export function DevPanel() {
  useApp(['doc', 'selection'], () => app.docVersion);
  const [format, setFormat] = useState<'css' | 'react' | 'html' | 'swiftui' | 'compose'>('css');
  const nodes = app.editor.selectedNodes();
  const n = nodes[0];
  const doc = app.doc;
  if (!n) {
    const ready = doc.children(app.editor.pageId).filter((f) => f.devStatus);
    return (
      <div className="panel right dev-panel" onPointerDown={(e) => e.stopPropagation()}>
        <div className="prop-section">
          <h4>Ready for development</h4>
          {ready.length === 0 && <div style={{ color: 'var(--text-2)' }}>No frames are marked ready on this page yet. Select a frame to mark it.</div>}
          {ready.map((f) => (
            <button key={f.id} className="dev-frame" onClick={() => (app.editor.setSelection([f.id]), app.zoomToNodes([f.id]))}>
              <span>{f.name}</span>
              {(changesSinceReady(doc, f.id)?.length ?? 0) > 0 && <span className="dev-changed">Changed</span>}
              <span className={`dev-status ${f.devStatus!.toLowerCase()}`}>{f.devStatus === 'READY' ? 'Ready' : 'Completed'}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }
  const parent = doc.get(n.parent);
  const isTop = parent?.type === 'PAGE' || parent?.type === 'SECTION';
  const style = n.characters !== undefined ? textStyle(n) : null;
  const code = generateCode(doc, nodes.map((x) => x.id), { format });
  // tokens as developers name them: the WEB code syntax (or the derived custom property)
  const vars = Object.entries(n.boundVariables ?? {}).map(([k, id]) => {
    const v = doc.get(id);
    return v ? `${k}: ${v.name} · var(${cssVariableName(v)})${v.codeSyntax?.ANDROID ? ` · ${v.codeSyntax.ANDROID}` : ''}${v.codeSyntax?.iOS ? ` · ${v.codeSyntax.iOS}` : ''}` : `${k}: ${id}`;
  });
  const styleNames = [n.fillStyleId, n.textStyleId, n.effectStyleId, n.strokeStyleId].filter(Boolean).map((id) => doc.get(id!)?.name ?? id!);
  const setStatus = (s: NodeData['devStatus']) => app.editor.update(n.id, { devStatus: s }, 'Dev status');
  const name = n.name.replace(/[^\w-]+/g, '-');
  const download = (blob: Blob, file: string) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = file;
    a.click();
  };
  return (
    <div className="panel right dev-panel" onPointerDown={(e) => e.stopPropagation()}>
      <div className="prop-section">
        <h4>
          <span>{n.name}</span>
          <span style={{ color: 'var(--text-2)' }}>{n.type.toLowerCase()}</span>
        </h4>
        {isTop && (n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE') && (
          <div className="row">
            <button className={`btn ${n.devStatus === 'READY' ? 'primary' : 'secondary'}`} onClick={() => setStatus(n.devStatus === 'READY' ? undefined : 'READY')}>
              {n.devStatus === 'READY' ? '✓ Ready for dev' : 'Mark ready for dev'}
            </button>
            {n.devStatus && (
              <button className="btn secondary" onClick={() => setStatus(n.devStatus === 'COMPLETED' ? 'READY' : 'COMPLETED')}>
                {n.devStatus === 'COMPLETED' ? 'Reopen' : 'Mark completed'}
              </button>
            )}
          </div>
        )}
        {n.devSnapshot && <CompareChanges n={n} />}
        <div className="dev-grid">
          <span>Width</span>
          <Copy text={`${r1(n.width)}px`} />
          <span>Height</span>
          <Copy text={`${r1(n.height)}px`} />
          <span>Top</span>
          <Copy text={`${r1(n.y)}px`} />
          <span>Left</span>
          <Copy text={`${r1(n.x)}px`} />
          {n.rotation ? (
            <>
              <span>Rotation</span>
              <Copy text={`${r1(n.rotation)}°`} />
            </>
          ) : null}
          {n.opacity < 1 && (
            <>
              <span>Opacity</span>
              <Copy text={`${Math.round(n.opacity * 100)}%`} />
            </>
          )}
          {n.cornerRadius ? (
            <>
              <span>Radius</span>
              <Copy text={Array.isArray(n.cornerRadius) ? n.cornerRadius.map((v) => `${v}px`).join(' ') : `${n.cornerRadius}px`} />
            </>
          ) : null}
        </div>
      </div>
      {isAutoLayout(n) && (
        <div className="prop-section">
          <h4>Auto layout</h4>
          <div className="dev-grid">
            <span>Direction</span>
            <span>{n.layoutMode === 'GRID' ? `grid · ${n.gridColumns?.length ?? 2} columns` : n.layoutMode!.toLowerCase()}</span>
            <span>Gap</span>
            <Copy text={`${n.itemSpacing ?? 0}px`} />
            <span>Padding</span>
            <Copy text={[n.paddingTop, n.paddingRight, n.paddingBottom, n.paddingLeft].map((v) => `${v ?? 0}px`).join(' ')} />
          </div>
        </div>
      )}
      {style && (
        <div className="prop-section">
          <h4>Typography</h4>
          <div className="dev-grid">
            <span>Font</span>
            <Copy text={style.fontFamily} />
            <span>Weight</span>
            <Copy text={String(style.fontWeight)} />
            <span>Size</span>
            <Copy text={`${style.fontSize}px`} />
            <span>Line height</span>
            <Copy text={`${r1(style.lineHeight)}px`} />
            {style.letterSpacing ? (
              <>
                <span>Letter spacing</span>
                <Copy text={`${style.letterSpacing}px`} />
              </>
            ) : null}
          </div>
          {n.characters && (
            <div style={{ marginTop: 6 }}>
              <Copy text={n.characters}>
                <span className="dev-text">{n.characters.slice(0, 160)}</span>
              </Copy>
            </div>
          )}
        </div>
      )}
      {(n.fills.length > 0 || n.strokes.length > 0) && (
        <div className="prop-section">
          <h4>Colors</h4>
          {[...n.fills.map((p) => ['Fill', p] as const), ...n.strokes.map((p) => ['Stroke', p] as const)].map(([kind, p], i) => (
            <div className="row" key={i}>
              <span className="dev-swatch" style={{ background: p.type === 'SOLID' ? colorToHex(p.color) : '#ccc' }} />
              <span style={{ width: 44, color: 'var(--text-2)' }}>{kind}</span>
              <Copy text={paintLabel(p)} />
            </div>
          ))}
        </div>
      )}
      {(vars.length > 0 || styleNames.length > 0) && (
        <div className="prop-section">
          <h4>Tokens</h4>
          {[...styleNames.map((s) => `style: ${s}`), ...vars].map((t) => (
            <div key={t}>
              <Copy text={t} />
            </div>
          ))}
        </div>
      )}
      {(n.type === 'INSTANCE' || n.type === 'COMPONENT' || n.isComponentSet) && <CodeConnectSection n={n} />}
      <PluginCode id={n.id} />
      <div className="prop-section">
        <h4>
          <span>Code</span>
          <select className="share-select" value={format} onChange={(e) => setFormat(e.target.value as 'css')}>
            <option value="css">CSS</option>
            <option value="react">React</option>
            <option value="html">HTML</option>
            <option value="swiftui">SwiftUI</option>
            <option value="compose">Jetpack Compose</option>
          </select>
        </h4>
        <pre className="dev-code">{code}</pre>
        <Copy text={code}>Copy code</Copy>
      </div>
      <div className="prop-section">
        <h4>Assets</h4>
        <div className="row">
          <button className="btn secondary" onClick={() => download(new Blob([toSvg(doc, nodes.map((x) => x.id), { shaderImage: (id, k, i) => app.shaders.dataUrl(id, k, i) })], { type: 'image/svg+xml' }), `${name}.svg`)}>
            SVG
          </button>
          {[1, 2, 3].map((s) => (
            <a key={s} className="btn secondary" href={`/api/files/${app.fileId}/render?ids=${nodes.map((x) => x.id).join(',')}&scale=${s}`} download={`${name}@${s}x.png`}>
              PNG {s}x
            </a>
          ))}
        </div>
      </div>
      <div className="prop-section">
        <h4>Annotations</h4>
        {(n.annotations ?? []).map((a, i) => (
          <div className="row" key={i} style={{ justifyContent: 'space-between' }}>
            <span>
              {a.label}
              {a.props?.length ? <span style={{ color: 'var(--text-2)' }}> · {a.props.join(', ')}</span> : null}
            </span>
            <button className="icon-btn" title="Remove" onClick={() => app.editor.update(n.id, { annotations: n.annotations!.filter((_, j) => j !== i).length ? n.annotations!.filter((_, j) => j !== i) : undefined }, 'Annotation')}>
              ✕
            </button>
          </div>
        ))}
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const label = String(f.get('label') ?? '').trim();
            if (!label) return;
            const props = String(f.get('props') ?? '').split(',').map((s) => s.trim()).filter(Boolean);
            app.editor.update(n.id, { annotations: [...(n.annotations ?? []), { label, ...(props.length ? { props } : {}) }] }, 'Annotation');
            e.currentTarget.reset();
          }}
        >
          <div className="field">
            <input name="label" placeholder="Add a note for developers" style={{ border: 0, background: 'transparent', outline: 'none', width: '100%' }} onKeyDown={(e) => e.stopPropagation()} />
          </div>
          <div className="field" style={{ flex: 'none', width: 80 }}>
            <input name="props" placeholder="props" title="Properties to call out, comma separated" style={{ border: 0, background: 'transparent', outline: 'none', width: '100%' }} onKeyDown={(e) => e.stopPropagation()} />
          </div>
        </form>
      </div>
    </div>
  );
}

/** Code Connect: the real component this design maps to, or a form to connect it. */
function CodeConnectSection({ n }: { n: NodeData }) {
  const doc = app.doc;
  const hit = codeConnectOf(doc, n.id);
  const [editing, setEditing] = useState(false);
  // the mapping lives on the component set (variants) or the component
  const comp = n.type === 'INSTANCE' ? doc.get(n.componentId ?? '') : n;
  const target = comp ? (comp.isComponentSet ? comp : (componentSetOf(doc, comp.id) ?? comp)) : null;
  if (!target) return null;
  const snippet = connectSnippet(doc, n.id);
  if (hit && !editing) {
    const code = [...(snippet?.imports ?? []), ...(snippet ? ['', snippet.jsx] : [])].join('\n').trim();
    return (
      <div className="prop-section">
        <h4>
          <span>Code Connect</span>
          <button className="btn ghost" onClick={() => setEditing(true)} disabled={app.readOnly}>
            Edit
          </button>
        </h4>
        <pre className="dev-code">{code}</pre>
        <div className="row">
          <Copy text={code}>Copy</Copy>
          {hit.mapping.source && /^https?:\/\//.test(hit.mapping.source) && (
            <a className="btn secondary" href={hit.mapping.source} target="_blank" rel="noreferrer noopener">
              View source
            </a>
          )}
        </div>
      </div>
    );
  }
  if (!editing)
    return (
      <div className="prop-section">
        <h4>Code Connect</h4>
        <div className="asset-hint">Link {target.name} to its code component so developers (and agents) see real usage.</div>
        <button className="btn secondary" onClick={() => setEditing(true)} disabled={app.readOnly}>
          Connect to code
        </button>
      </div>
    );
  const cur = target.codeConnect;
  return (
    <form
      className="prop-section"
      onSubmit={(e) => {
        e.preventDefault();
        const f = new FormData(e.currentTarget);
        const component = String(f.get('component') ?? '').trim();
        if (!component) return;
        const imp = String(f.get('import') ?? '').trim();
        const source = String(f.get('source') ?? '').trim();
        app.editor.update(target.id, { codeConnect: { ...cur, component, import: imp || undefined, source: source || undefined } }, 'Code Connect');
        setEditing(false);
      }}
    >
      <h4>Code Connect</h4>
      <label className="cc-field">
        <span>Component</span>
        <input name="component" defaultValue={cur?.component ?? target.name.replace(/[^A-Za-z0-9]+(.)?/g, (_, c: string) => (c ? c.toUpperCase() : '')).replace(/^./, (c) => c.toUpperCase())} />
      </label>
      <label className="cc-field">
        <span>Import</span>
        <input name="import" defaultValue={cur?.import ?? ''} placeholder="import { Button } from '@/ui/button'" />
      </label>
      <label className="cc-field">
        <span>Source</span>
        <input name="source" defaultValue={cur?.source ?? ''} placeholder="https://github.com/…/button.tsx" />
      </label>
      <div className="row">
        <button className="btn primary" type="submit">
          Save
        </button>
        <button className="btn secondary" type="button" onClick={() => setEditing(false)}>
          Cancel
        </button>
        {cur && (
          <button className="btn ghost" type="button" onClick={() => (app.editor.update(target.id, { codeConnect: undefined }, 'Code Connect'), setEditing(false))}>
            Disconnect
          </button>
        )}
      </div>
    </form>
  );
}

/** Changes since the frame was marked ready: a list, and before/after side by side. */
function CompareChanges({ n }: { n: NodeData }) {
  const [open, setOpen] = useState(false);
  const changes = changesSinceReady(app.doc, n.id) ?? [];
  const when = new Date(n.devSnapshot!.at).toLocaleString();
  return (
    <>
      <div className="row" style={{ alignItems: 'center' }}>
        <span style={{ flex: 1, color: 'var(--text-2)' }}>{changes.length ? `${changes.length} change${changes.length > 1 ? 's' : ''} since ready` : 'No changes since ready'}</span>
        {changes.length > 0 && (
          <button className="btn secondary" onClick={() => setOpen(true)}>
            Compare
          </button>
        )}
      </div>
      {open && (
        <div className="modal-backdrop" onPointerDown={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div className="modal" style={{ width: 'min(920px, calc(100vw - 32px))' }}>
            <header>
              <span>
                {n.name}: changes since ready ({when})
              </span>
              <button className="icon-btn" onClick={() => setOpen(false)}>
                ✕
              </button>
            </header>
            <div className="body">
              <div className="compare-pair">
                <figure>
                  <Snapshot nodes={n.devSnapshot!.nodes} rootId={n.id} />
                  <figcaption>Ready for dev</figcaption>
                </figure>
                <figure>
                  <Snapshot nodes={app.doc.subtree(n.id)} rootId={n.id} />
                  <figcaption>Now</figcaption>
                </figure>
              </div>
              {changes.map((c) => (
                <div key={c.nodeId + c.kind} className="merge-row">
                  <span className={`merge-kind ${c.kind}`}>{c.kind}</span>
                  <span style={{ flex: 1 }}>{c.name}</span>
                  <span style={{ color: 'var(--text-2)' }}>{c.props?.join(', ')}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Draw a frame (from a snapshot or the live file) into a fixed-size preview. */
function Snapshot({ nodes, rootId }: { nodes: NodeData[]; rootId: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current!;
    const dpr = window.devicePixelRatio || 1;
    const W = 420,
      H = 320;
    c.width = W * dpr;
    c.height = H * dpr;
    try {
      // a throwaway document holding just this frame, placed at the origin
      const { doc, pageId: page } = new Editor();
      doc.apply({ ops: nodes.map((x) => ({ op: 'create', node: { ...structuredClone(x), ...(x.id === rootId ? { parent: page, x: 0, y: 0, devSnapshot: undefined } : {}) } })) }, { remote: true });
      const b = boundsOfNodes(doc, [rootId]);
      if (!b) return;
      const s = Math.min((W - 16) / b.width, (H - 16) / b.height, 2) * dpr;
      new Renderer2D(doc).renderPage(c.getContext('2d')!, page, { transform: [s, 0, 0, s, (c.width - b.width * s) / 2 - b.x * s, (c.height - b.height * s) / 2 - b.y * s], background: false, images: app.images });
    } catch (e) {
      console.warn('[vellum] preview failed', e);
    }
  }, [nodes, rootId]);
  return <canvas ref={ref} style={{ width: 420, height: 320 }} />;
}

/** Code from a running codegen plugin (figma.codegen.on('generate')), for the selected layer. */
function PluginCode({ id }: { id: string }) {
  const gen = useApp('ui', () => app.codegen);
  const results = useApp('ui', () => app.codegenResults.get(id));
  useEffect(() => {
    if (gen) app.requestCodegen(id);
  }, [gen, id, app.docVersion]);
  if (!gen) return null;
  return (
    <div className="prop-section">
      <h4>
        <span>{gen.name}</span>
        <span style={{ color: 'var(--text-2)' }}>plugin</span>
      </h4>
      {!results && <div style={{ color: 'var(--text-2)' }}>Generating…</div>}
      {results?.length === 0 && <div style={{ color: 'var(--text-2)' }}>No code for this layer.</div>}
      {results?.map((r, i) => (
        <div key={i}>
          {r.title && <div style={{ color: 'var(--text-2)', margin: '4px 0' }}>{r.title}</div>}
          <pre className="dev-code">{r.code}</pre>
          <Copy text={r.code}>Copy</Copy>
        </div>
      ))}
    </div>
  );
}
